import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { CardModule } from 'primeng/card';
import { ButtonModule } from 'primeng/button';
import { InputTextModule } from 'primeng/inputtext';
import { ToastModule } from 'primeng/toast';
import { MessageService } from 'primeng/api';
import { TagModule } from 'primeng/tag';
import { SkeletonModule } from 'primeng/skeleton';
import { DividerModule } from 'primeng/divider';
import { AppConfigService, AppConfigDto } from '../../services/app-config.service';

interface EditableConfig extends AppConfigDto {
  editValue: string;
  dirty: boolean;
  saving: boolean;
}

@Component({
  selector: 'app-app-config',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    CardModule,
    ButtonModule,
    InputTextModule,
    ToastModule,
    TagModule,
    SkeletonModule,
    DividerModule,
  ],
  providers: [MessageService],
  templateUrl: './app-config.component.html',
  styleUrls: ['./app-config.component.scss'],
})
export class AppConfigComponent implements OnInit {
  configs: EditableConfig[] = [];
  loading = true;
  saving = false;
  groupedConfigs: { category: string; items: EditableConfig[] }[] = [];

  get hasChanges(): boolean {
    return this.configs.some((c) => c.dirty);
  }

  get changedCount(): number {
    return this.configs.filter((c) => c.dirty).length;
  }

  constructor(
    private appConfigService: AppConfigService,
    private messageService: MessageService,
  ) {}

  ngOnInit(): void {
    this.loadConfigs();
  }

  loadConfigs(): void {
    this.loading = true;
    this.appConfigService.getAllConfigs().subscribe({
      next: (data) => {
        this.configs = data.map((c) => ({
          ...c,
          editValue: c.value,
          dirty: false,
          saving: false,
        }));
        this.updateGroupedConfigs();
        this.loading = false;
      },
      error: (err) => {
        console.error('Failed to load configs', err);
        this.messageService.add({
          severity: 'error',
          summary: 'Error',
          detail: 'Failed to load configuration. Please try again.',
        });
        this.loading = false;
      },
    });
  }

  onValueChange(config: EditableConfig): void {
    config.dirty = config.editValue !== config.value;
  }

  resetConfig(config: EditableConfig): void {
    config.editValue = config.value;
    config.dirty = false;
  }

  resetAll(): void {
    this.configs.forEach((c) => this.resetConfig(c));
  }

  saveAll(): void {
    const changed = this.configs.filter((c) => c.dirty);
    if (changed.length === 0) return;

    this.saving = true;
    const payload = changed.map((c) => ({ key: c.key, value: c.editValue.toString() }));

    this.appConfigService.updateConfigs(payload).subscribe({
      next: (updated) => {
        this.configs = updated.map((c) => ({
          ...c,
          editValue: c.value,
          dirty: false,
          saving: false,
        }));
        this.updateGroupedConfigs();
        this.saving = false;
        this.messageService.add({
          severity: 'success',
          summary: 'Saved',
          detail: `${changed.length} configuration${changed.length > 1 ? 's' : ''} updated successfully.`,
        });
      },
      error: (err) => {
        console.error('Failed to save configs', err);
        this.saving = false;
        this.messageService.add({
          severity: 'error',
          summary: 'Error',
          detail: 'Failed to save configuration. Please try again.',
        });
      },
    });
  }

  getConfigCategory(key: string): string {
    if (key.includes('UPDATE')) return 'Deployment Limits';
    if (key.includes('VIEW') || key === 'MAX_MACHINES_SW_DEV') return 'View Limits';
    return 'Other';
  }

  private updateGroupedConfigs(): void {
    const groups: Record<string, EditableConfig[]> = {};
    for (const config of this.configs) {
      const category = this.getConfigCategory(config.key);
      if (!groups[category]) groups[category] = [];
      groups[category].push(config);
    }
    this.groupedConfigs = Object.entries(groups).map(([category, items]) => ({
      category,
      items,
    }));
  }

  getGroupedConfigs(): { category: string; items: EditableConfig[] }[] {
    return this.groupedConfigs;
  }
}
